import { AccessTimeFilled } from '@mui/icons-material';
import { Box, Theme, Typography, useMediaQuery } from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';

import { ListenerCount, PageTabs } from '@/components';
import { ComplianceTypes } from '@/constants';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';
import {
  selectEpisodeCompliance,
  selectSessionUserId,
  useAppSelector,
  useFetchListenerDataQuery,
} from '@/store';
import { Episode, Referrer } from '@/types';
import { convertSecondsToHoursMinutes } from '@/utils';

import { FreeTrialPrompt } from '../FreeTrialPrompt';
import {
  AdditionalResources,
  AppHighlights,
  ArchivalDisclaimer,
  DemoPrompt,
  EpisodeCategories,
  EpisodeChapterList,
  EpisodeControlButtons,
  EpisodeCoverImage,
  EpisodeDescription,
  EpisodeHeader,
  RelatedEpisodes,
  RelatedLearningPaths,
  Speakers,
} from './components';

export const MOBILE_TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'chapters', label: 'Chapters & Quizzes' },
];
const referrer = { page: EVENT_CONSTANTS.PAGE.EPISODE_DETAILS };

const AnonymousInfo = ({ referrer }: { referrer: Referrer }) => {
  return (
    <Box>
      <FreeTrialPrompt referrer={referrer} />
      <AppHighlights />
      <DemoPrompt />
    </Box>
  );
};

export const EpisodeDetails = ({
  episode,
  customControls,
}: {
  episode: Episode;
  customControls?: React.ReactNode;
}) => {
  const [selectedTab, setSelectedTab] = useState('overview');
  const isSmDown = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  const userId = useSelector(selectSessionUserId);

  const { data: listenerData } = useFetchListenerDataQuery(
    { episodeIds: [episode.episodeId] },
    { skip: !userId },
  );

  const compliance = useAppSelector(state =>
    selectEpisodeCompliance(state, episode?.episodeId),
  );

  if (isSmDown) {
    return (
      <Box sx={styles.rootMobile}>
        <EpisodeCoverImage episode={episode} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          {compliance === ComplianceTypes.CPE && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <AccessTimeFilled sx={{ fontSize: 16, color: 'text.light' }} />
              <Typography variant="caption1Bold" color="text.light">
                {convertSecondsToHoursMinutes(episode.duration, true)}
              </Typography>
            </Box>
          )}
          <ListenerCount listeners={listenerData?.[0]} slim />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <EpisodeHeader episode={episode} />
          {customControls || (
            <EpisodeControlButtons episode={episode} referrer={referrer} />
          )}
        </Box>
        <PageTabs
          tabs={MOBILE_TABS}
          selectedTab={selectedTab}
          onChange={(e, newVal) => {
            AnalyticsService.episodeDetailSwitchTab({
              episodeId: episode.episodeId,
              tabName: newVal,
            });
            setSelectedTab(newVal);
          }}
        />
        {selectedTab === MOBILE_TABS[0].value && (
          <>
            <ArchivalDisclaimer episodeId={episode.episodeId} />
            <EpisodeDescription episode={episode} />
          </>
        )}
        {selectedTab === MOBILE_TABS[1].value && (
          <>
            <EpisodeChapterList episode={episode} referrer={referrer} />
            <AdditionalResources episode={episode} />
            <Speakers episode={episode} />
          </>
        )}
        <EpisodeCategories episode={episode} />
        <RelatedLearningPaths
          episodeId={episode.episodeId}
          referrer={referrer}
        />
        <RelatedEpisodes episode={episode} referrer={referrer} />
        {!userId && <AnonymousInfo referrer={referrer} />}
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={styles.root}>
        <Box sx={styles.leftColumn}>
          <Box sx={styles.topLeftCell}>
            <EpisodeHeader episode={episode} />
            {customControls || (
              <EpisodeControlButtons episode={episode} referrer={referrer} />
            )}
          </Box>
          <ArchivalDisclaimer episodeId={episode.episodeId} />
          <EpisodeDescription episode={episode} />
          <Speakers episode={episode} />
          <EpisodeCategories episode={episode} />
          <RelatedEpisodes episode={episode} referrer={referrer} />
        </Box>
        <Box sx={styles.rightColumn}>
          <Box sx={styles.topRigthCell}>
            <EpisodeCoverImage episode={episode} />
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {compliance === ComplianceTypes.CPE && (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <AccessTimeFilled
                    sx={{ fontSize: 16, color: 'secondary.main' }}
                  />
                  <Typography variant="body3">
                    {convertSecondsToHoursMinutes(episode.duration, true)}
                  </Typography>
                </Box>
              )}
              <ListenerCount listeners={listenerData?.[0]} />
            </Box>
          </Box>
          <EpisodeChapterList episode={episode} referrer={referrer} />
          <AdditionalResources episode={episode} />
          <RelatedLearningPaths
            episodeId={episode.episodeId}
            referrer={referrer}
          />
        </Box>
      </Box>
      {!userId && <AnonymousInfo referrer={referrer} />}
    </Box>
  );
};

export const styles = {
  root: {
    display: 'flex',
    flexDirection: 'row',
  },
  rootMobile: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  leftColumn: {
    flex: 3, //left column is 60% of the screen
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  rightColumn: {
    display: 'flex',
    flex: 2, //right column is 40% of the screen
    flexDirection: 'column',
    ml: 6,
    maxWidth: 480,
    minWidth: 240,
    gap: 5,
  },
  topLeftCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  topRigthCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
};
